import React from 'react';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';
import { Grid, Segment, Button } from 'semantic-ui-react';
import { isEmpty, without } from 'lodash';
import {
  SidebarPortal,
  BlocksToolbar,
  Icon,
  BlockDataForm,
  BlocksForm,
} from '@plone/volto/components';
import {
  emptyBlocksForm,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers';
import { setSidebarTab } from '@plone/volto/actions';
import { connect } from 'react-redux';
import config from '@plone/volto/registry';
import cx from 'classnames';

import { ColumnsBlockSchema } from './schema';
import {
  getColumns,
  empty,
  defaultNewColumn,
  hasColumns,
  forEachColumn,
  columnIsEmpty,
} from '@eeacms/volto-columns-block/ColumnsBlock/utils';
import ColumnVariations from '@eeacms/volto-columns-block/ColumnsBlock/ColumnVariations';
import EditBlockWrapper from '@eeacms/volto-columns-block/ColumnsBlock/EditBlockWrapper';

import { COLUMNSBLOCK } from '@eeacms/volto-columns-block/constants';
import { makeStyleSchema, getStyle } from '@eeacms/volto-columns-block/Styles';

import tuneSVG from '@plone/volto/icons/column.svg';
import upSVG from '@plone/volto/icons/up.svg';
import eraserSVG from '@eeacms/volto-columns-block/ColumnsBlock/icons/eraser.svg';
import '@eeacms/volto-columns-block/less/columns.less';

const messages = defineMessages({
  labelColumn: {
    id: 'Column',
    defaultMessage: 'Column',
  },
  labelColumnsBlock: {
    id: 'Columns block',
    defaultMessage: 'Columns block',
  },
  labelToColSettings: {
    id: 'Go to Column settings',
    defaultMessage: 'Go to Column settings',
  },
});

class ColumnsBlockEdit extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      multiSelected: [],
      colSelections: {},
      showSidebar: false,
      activeColumn: null,
    };
    this.blocksState = {};
  }

  createFrom = (initialData) => {
    const { gridCols, gridSize } = initialData;
    return {
      data: empty(gridCols.length),
      gridSize,
      gridCols,
    };
  };

  handleKeyDown = (
    e,
    index,
    block,
    node,
    {
      disableEnter = false,
      disableArrowUp = false,
      disableArrowDown = false,
    } = {},
  ) => {
    const hasblockActive = Object.keys(this.state.colSelections).length > 0;
    if (e.key === 'ArrowUp' && !disableArrowUp && !hasblockActive) {
      this.props.onFocusPreviousBlock(block, node);
      e.preventDefault();
    }
    if (e.key === 'ArrowDown' && !disableArrowDown && !hasblockActive) {
      this.props.onFocusNextBlock(block, node);
      e.preventDefault();
    }
    if (e.key === 'Enter' && !disableEnter && !hasblockActive) {
      this.props.onAddBlock(config.settings.defaultBlockType, index + 1);
      e.preventDefault();
    }
  };

  onChangeColumnSettings = (id, value) => {
    const { data, onChangeBlock, block } = this.props;
    const coldata = data.data;
    const formData = {
      ...data,
      data: {
        ...coldata,
        blocks: {
          ...coldata.blocks,
          [this.state.activeColumn]: {
            ...coldata.blocks?.[this.state.activeColumn],
            settings: {
              ...coldata.blocks?.[this.state.activeColumn]?.settings,
              [id]: value,
            },
          },
        },
      },
    };
    onChangeBlock(block, formData);
  };

  onChangeColumnData = (id, value, colId) => {
    const { data, onChangeBlock, block, onChangeField } = this.props;
    const coldata = data.data;
    if (['blocks', 'blocks_layout'].indexOf(id) > -1) {
      this.blocksState[id] = value;
      onChangeBlock(block, {
        ...data,
        data: {
          ...coldata,
          blocks: {
            ...coldata.blocks,
            [colId]: {
              ...coldata.blocks?.[colId],
              ...this.blocksState,
            },
          },
        },
      });
    } else {
      onChangeField(id, value);
    }
  };

  deleteColumnStyle = () => {
    const colId = this.state.activeColumn;
    const { onChangeBlock, block, data } = this.props;
    const coldata = data.data;
    onChangeBlock(block, {
      ...data,
      data: {
        ...coldata,
        blocks: {
          ...coldata.blocks,
          [colId]: {
            ...coldata.blocks?.[colId],
            settings: {},
          },
        },
      },
    });
  };

  onSelectBlock = (
    id,
    colId,
    colData,
    activeBlock,
    isMultipleSelection,
    event,
  ) => {
    let newMultiSelected = [];
    let selected = id;

    if (isMultipleSelection) {
      selected = null;
      const blocksLayoutFieldname = getBlocksLayoutFieldname(colData);
      const blocks_layout = colData[blocksLayoutFieldname].items;

      if (event.shiftKey) {
        const anchor =
          this.state.multiSelected.length > 0
            ? blocks_layout.indexOf(this.state.multiSelected[0])
            : blocks_layout.indexOf(activeBlock);
        const focus = blocks_layout.indexOf(id);

        if (anchor === focus) {
          newMultiSelected = [id];
        } else if (focus > anchor) {
          newMultiSelected = [...blocks_layout.slice(anchor, focus + 1)];
        } else {
          newMultiSelected = [...blocks_layout.slice(focus, anchor + 1)];
        }
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
        if (this.state.multiSelected.includes(id)) {
          selected = null;
          newMultiSelected = without(this.state.multiSelected, id);
        } else {
          newMultiSelected = [...(this.state.multiSelected || []), id];
        }
      }
    }

    this.setState({
      multiSelected: newMultiSelected,
      colSelections: { [colId]: selected },
    });
  };

  getColumnsBlockSchema = () => {
    const variants = config.blocks.blocksConfig?.[COLUMNSBLOCK]?.variants || [];
    const schema = ColumnsBlockSchema(this.props.intl);
    const { data } = this.props;
    const { blocks_layout = {} } = data.data || {};
    const nrOfColumns = (blocks_layout?.items || []).length;
    const available_variants = variants.filter(
      ({ defaultData }) => defaultData?.gridCols?.length === nrOfColumns,
    );

    schema.properties.gridCols.choices = available_variants.map(
      ({ defaultData, title }) => [defaultData?.gridCols, title],
    );
    return schema;
  };

  componentDidUpdate(prevProps) {
    const variants = config.blocks.blocksConfig?.[COLUMNSBLOCK]?.variants || [];
    const cols = this.props.data.data?.blocks_layout?.items || [];
    const prevCols = prevProps.data.data?.blocks_layout?.items || [];

    const colNumChanged = cols.length !== prevCols.length;
    const initialLayoutSelection = prevCols.length === 0;
    const shouldUpdateLayout = colNumChanged && !initialLayoutSelection;

    if (shouldUpdateLayout) {
      const available_variants = variants.filter(
        ({ defaultData }) => defaultData?.gridCols?.length === cols.length,
      );
      const variant = available_variants?.[0];
      if (variant) {
        return this.props.onChangeBlock(this.props.block, {
          ...this.props.data,
          gridSize: variant.defaultData.gridSize,
          gridCols: variant.defaultData.gridCols,
        });
      }
    }

    const { block, onChangeBlock, data } = this.props;
    if (hasColumns(data.data)) {
      forEachColumn(data.data, ([colId, colData]) => {
        if (columnIsEmpty(colData)) {
          const newData = {
            ...data,
            data: {
              ...data.data,
              blocks: {
                ...data.data.blocks,
                [colId]: defaultNewColumn(),
              },
            },
          };
          onChangeBlock(block, newData);
        }
      });
    }
  }

  render() {
    const { block, data, onChangeBlock, pathname, selected, manage } =
      this.props;
    const metadata = this.props.metadata || this.props.properties;
    const { gridCols, gridSize } = data;
    const coldata = data.data;
    const columnList = getColumns(coldata);
    const selectedCol =
      Object.keys(this.state.colSelections).length > 0
        ? Object.keys(this.state.colSelections)[0]
        : null;
    const selectedColData = coldata?.blocks?.[selectedCol] || null;
    const selectedBlock = this.state.colSelections[selectedCol];
    const { gridSizes, variants, available_colors } =
      config.blocks.blocksConfig[COLUMNSBLOCK];
    const ColumnSchema = makeStyleSchema({ available_colors }, this.props.intl);
    const isInitialized = data?.data && Object.keys(data?.data).length > 0;
    const bg = data?.background_color || 'white';

    return (
      <div
        role="presentation"
        className={cx('columns-block', `columns--${bg}`)}
        onKeyDown={(e) => {
          this.handleKeyDown(
            e,
            this.props.index,
            this.props.block,
            this.props.blockNode.current,
          );
        }}
        tabIndex={-1}
      >
        {!isInitialized ? (
          <ColumnVariations
            variants={variants.filter((variant) => variant.common)}
            onChange={(initialData) => {
              onChangeBlock(block, {
                ...data,
                ...this.createFrom(initialData),
              });
            }}
          />
        ) : (
          <>
            <div
              className="columns-header"
              onClick={() => {
                this.setState({ showSidebar: true, colSelections: {} });
                this.props.setSidebarTab(1);
              }}
              aria-hidden="true"
            >
              {data.title || (
                <FormattedMessage
                  id="Columns block"
                  defaultMessage="Columns block"
                />
              )}
            </div>
            <Grid columns={gridSize} className="column-grid" stackable>
              {columnList.map(([colId, column], index) => (
                <Grid.Column
                  className={cx(
                    'block-column',
                    data?.data?.blocks?.[colId]?.settings?.column_class,
                  )}
                  key={colId}
                  {...(gridSizes[gridCols[index]] || gridCols[index])}
                  {...getStyle(data?.data?.blocks?.[colId]?.settings || {})}
                >
                  <BlocksForm
                    errors={this.props.errors}
                    key={colId}
                    title={data?.placeholder}
                    description={data?.instructions?.data}
                    manage={manage}
                    allowedBlocks={data?.allowedBlocks}
                    metadata={metadata}
                    properties={{
                      ...metadata,
                      ...(isEmpty(column) ? emptyBlocksForm() : column),
                    }}
                    disableEvents={true}
                    selectedBlock={
                      selected ? this.state.colSelections[colId] : null
                    }
                    onSelectBlock={(id, selected, e) => {
                      const isMultipleSelection =
                        e?.shiftKey || e?.ctrlKey || e?.metaKey;
                      this.onSelectBlock(
                        id,
                        colId,
                        selectedColData,
                        selectedBlock,
                        selectedCol !== colId || selectedBlock === id
                          ? false
                          : isMultipleSelection,
                        e,
                      );
                    }}
                    onChangeFormData={(newFormData) => {
                      onChangeBlock(block, {
                        ...data,
                        data: {
                          ...coldata,
                          blocks: {
                            ...coldata.blocks,
                            [colId]: {
                              ...coldata.blocks?.[colId],
                              blocks: newFormData.blocks,
                              blocks_layout: newFormData.blocks_layout,
                            },
                          },
                        },
                      });
                    }}
                    onChangeField={(id, value) =>
                      this.onChangeColumnData(id, value, colId)
                    }
                    pathname={pathname}
                  >
                    {({ draginfo }, editBlock, blockProps) => (
                      <EditBlockWrapper
                        draginfo={draginfo}
                        blockProps={blockProps}
                        extraControls={
                          <>
                            {!data?.readOnlySettings && (
                              <Button
                                icon
                                basic
                                title={this.props.intl.formatMessage(
                                  messages.labelToColSettings,
                                )}
                                onClick={() => {
                                  this.setState({
                                    showSidebar: true,
                                    activeColumn: colId,
                                    colSelections: {},
                                  });
                                  this.props.setSidebarTab(1);
                                }}
                              >
                                <Icon name={tuneSVG} className="" size="19px" />
                              </Button>
                            )}
                          </>
                        }
                        multiSelected={this.state.multiSelected.includes(
                          blockProps.block,
                        )}
                      >
                        {editBlock}
                      </EditBlockWrapper>
                    )}
                  </BlocksForm>
                </Grid.Column>
              ))}
            </Grid>
          </>
        )}

        {selected && selectedColData ? (
          <BlocksToolbar
            formData={selectedColData}
            selectedBlock={selectedBlock}
            selectedBlocks={this.state.multiSelected}
            onChangeBlocks={(newBlockData) => {
              onChangeBlock(block, {
                ...data,
                data: {
                  ...coldata,
                  blocks: {
                    ...coldata.blocks,
                    [selectedCol]: { ...selectedColData, ...newBlockData },
                  },
                },
              });
            }}
            onSetSelectedBlocks={(blockIds) => {
              this.setState({ multiSelected: blockIds });
            }}
            onSelectBlock={this.onSelectBlock}
          />
        ) : (
          ''
        )}

        {Object.keys(this.state.colSelections).length === 0 &&
        !data?.readOnlySettings ? (
          <SidebarPortal selected={selected}>
            {this.state.activeColumn ? (
              <>
                <Segment
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Button onClick={() => this.setState({ activeColumn: null })}>
                    <Icon name={upSVG} size="14px" />
                    <FormattedMessage
                      id="Edit parent columns block"
                      defaultMessage="Edit parent columns block"
                    />
                  </Button>
                  <button
                    onClick={this.deleteColumnStyle}
                    style={{ cursor: 'pointer' }}
                  >
                    <Icon
                      name={eraserSVG}
                      size="24px"
                      title="Clear column style"
                    />
                  </button>
                </Segment>
                <BlockDataForm
                  schema={ColumnSchema}
                  title={`${this.props.intl.formatMessage(messages.labelColumn)} ${
                    columnList
                      .map(([colId]) => colId)
                      .indexOf(this.state.activeColumn) + 1
                  }`}
                  onChangeField={this.onChangeColumnSettings}
                  formData={
                    data?.data?.blocks?.[this.state.activeColumn]?.settings ||
                    {}
                  }
                />
              </>
            ) : (
              <BlockDataForm
                schema={this.getColumnsBlockSchema()}
                title={this.props.intl.formatMessage(
                  messages.labelColumnsBlock,
                )}
                onChangeField={(id, value) => {
                  onChangeBlock(block, { ...data, [id]: value });
                }}
                formData={data}
              />
            )}
          </SidebarPortal>
        ) : (
          ''
        )}
      </div>
    );
  }
}

export default compose(
  injectIntl,
  connect(null, { setSidebarTab }),
)(ColumnsBlockEdit);
